<script setup lang="ts">
/**
 * Dashboard "Browse Libraries" cards (BPE-599): one card per library the
 * learner can actually access, opening that library's typed browse page.
 * Replaces the six hardcoded format×type cards (Live CLE, On-Demand CLE, …) —
 * Format is a filter inside the library page now, and a card for a library the
 * learner has no contract for helps nobody.
 */
import { computed, onMounted } from 'vue';
import LibraryCard from './LibraryCard.vue';
import { useAccessibleLibraryStore } from '@/stores/accessibleLibraryStore';
import { BETA_LABEL, isBetaKind, tagIcon } from '@/utils/credentialTypes';

const accessibleLibraryStore = useAccessibleLibraryStore();

// Per-kind card presentation. A kind absent from this map falls through to the
// neutral presentation below rather than borrowing another kind's identity.
// The icon is the kind's type-tag icon (tagIcon), so card and tag always agree.
const CARD_PRESENTATION: Record<string, { icon: string; variant: 'cle' | 'cpe' | 'skills' | 'microlearning' | 'simulation'; description: string }> = {
  CLE: { icon: tagIcon('cle'), variant: 'cle', description: 'Continuing Legal Education courses for attorneys' },
  CPE: { icon: tagIcon('cpe'), variant: 'cpe', description: 'Continuing Professional Education for accountants' },
  Skills: { icon: tagIcon('skills'), variant: 'skills', description: 'Professional development and soft skills training' },
  Microlearning: { icon: tagIcon('microlearning'), variant: 'microlearning', description: 'Quick, focused learning modules' },
  Simulation: { icon: tagIcon('simulation'), variant: 'simulation', description: 'Interactive role-play scenarios' },
};

const cards = computed(() =>
  accessibleLibraryStore.libraries.map((library) => {
    // Neutral, not 'skills'. The old fallback dressed any unrecognised kind in
    // Skills' cyan — so a Simulations library read as a Skills one, which is
    // exactly what PRD R3 forbids ("no screen shows a simulation wearing another
    // type's colour"). Simulation has its own accent now (BPE-700), but the
    // fallback stays: a plain card for an unrecognised kind is honest, and it
    // keeps the next new kind honest too.
    const presentation = (library.kind && CARD_PRESENTATION[library.kind]) || {
      icon: 'library_books',
      variant: 'neutral' as const,
      description: 'Explore courses and learning content',
    };
    return {
      to: `/browse/libraries/${library.id}`,
      icon: presentation.icon,
      title: library.effectiveDisplayName,
      description: library.description ?? presentation.description,
      variant: presentation.variant,
      tag: isBetaKind(library.kind) ? BETA_LABEL : undefined,
    };
  }),
);

onMounted(() => {
  accessibleLibraryStore.ensureLoaded();
});
</script>

<template>
  <div v-if="cards.length > 0" class="dashboard-section dashboard-section-full browse-libraries-section">
    <div class="section-header">
      <h2>Browse Libraries</h2>
    </div>
    <div class="library-browse-grid">
      <LibraryCard
        v-for="card in cards"
        :key="card.to"
        v-bind="card"
      />
    </div>
  </div>
</template>
